import { ZAMIAR_MOJEGO_WYSZUKIWANIA } from '@/lib/serpUrl';
/**
 * Which top-navigation variant to render. Derived from the auth session + the
 * user's DB role. Mirrors the three states in the design mockups (logged-out,
 * tenant, owner) plus the minimal agent account.
 */
export type NavState = 'logged_out' | 'tenant' | 'owner' | 'agent';

export function resolveNavState(
  hasSession: boolean,
  role: string | null | undefined,
): NavState {
  if (!hasSession) return 'logged_out';
  // Agents are a soft register in v1: they get their OWN stripped nav, not the
  // owner one. Must be checked BEFORE the owner branch — they used to share it,
  // which handed them the whole landlord console (Maciek 2026-07-29).
  if (role === 'agent') return 'agent';
  if (role === 'owner') return 'owner';
  return 'tenant';
}

export interface NavItem {
  /** i18n key under `header.*`. */
  key: string;
  /** Route to navigate to. `/coming-soon` is the placeholder for features
   *  whose page isn't built yet (messages, help, search-tenants, …). */
  to: string;
  /** Icon name resolved to a lucide component in the Header. */
  icon?: NavIcon;
  /** Stan wpisu historii przekazywany linkiem (nie trafia do adresu). */
  state?: unknown;
}

export type NavIcon = 'search' | 'messages' | 'bell' | 'help' | 'calendar' | 'heart';

const PLACEHOLDER = '/coming-soon';

/**
 * Primary nav items — the ones shown inline on the desktop top bar (and at the
 * top of the mobile drawer). The profile/flat entry and the secondary menu
 * (help/settings/language/logout) are rendered separately by the Header.
 */
export function getPrimaryNavItems(
  state: NavState,
  { maUlubione = false }: { maUlubione?: boolean } = {},
): NavItem[] {
  switch (state) {
    case 'tenant':
      // Serce stoi obok czatu i dzwonka, bo to jest ten sam rodzaj wejścia: „moje rzeczy",
      // nie nawigacja po produkcie. Przed wyszukiwarką go nie ma — pusta lista ulubionych
      // na pierwszym miejscu w pasku nie ma czego zapraszać.
      return [
        // Świadome wejście na wyszukiwarkę: wczytuje „Moje wyszukiwanie” nawet po „Wyczyść”
        // w tej sesji (zgłoszenie Maćka 1.10; useStanWyszukiwania).
        { key: 'search_flats', to: '/search', icon: 'search', state: ZAMIAR_MOJEGO_WYSZUKIWANIA },
        { key: 'favorites', to: '/ulubione', icon: 'heart' },
        { key: 'messages', to: '/messages', icon: 'messages' },
        { key: 'notifications', to: '/notifications', icon: 'bell' },
      ];
    case 'owner':
      // "Szukaj Najemcy" sends owners to the community of real tenants, where they can
      // open a tenant's profile and start a conversation (Messaging.md §2.1).
      return [
        { key: 'search_tenants', to: '/community', icon: 'search' },
        { key: 'messages', to: '/messages', icon: 'messages' },
        { key: 'notifications', to: '/notifications', icon: 'bell' },
      ];
    // Agents can't do anything in the product yet (no search, no messaging, no
    // notifications) — their whole surface is the coming-soon splash.
    case 'agent':
      return [];
    case 'logged_out':
      // Pasek niezalogowanego jest marketingowy i ma taki zostać — trasy aplikacji (profil,
      // wiadomości, ustawienia, powiadomienia) nie mają tu czego szukać. „Ulubione" są
      // JAWNYM wyjątkiem i to nie jest złamanie tamtej reguły: to nie jest link do
      // aplikacji, tylko dojście do rzeczy TEGO GOŚCIA, która leży w jego przeglądarce.
      // Bez tego dojścia zachęta „załóż konto, żeby mieć listę wszędzie" nie ma sensu, bo
      // nikt tej listy nie zobaczy.
      //
      // Warunek `maUlubione`: pusta lista nie potrzebuje ikony w pasku, który poza tym
      // sprzedaje produkt (decyzja Piotra). Serce pojawia się dopiero, gdy jest co pokazać.
      return [
        ...(maUlubione ? [{ key: 'favorites', to: '/ulubione', icon: 'heart' as const }] : []),
        { key: 'nav_flats_warsaw', to: PLACEHOLDER },
        { key: 'nav_community', to: '/community' },
        { key: 'nav_for_tenants', to: PLACEHOLDER },
        { key: 'nav_for_owners', to: PLACEHOLDER },
      ];
  }
}

/**
 * Secondary menu items behind the hamburger (logged-in states), in order. The
 * language switcher and logout are appended by the Header itself because they
 * are not plain links. Per the mockup the language selector sits directly
 * under Settings.
 */
export function getMenuItems(state: NavState): NavItem[] {
  if (state === 'logged_out') return [];
  // Agents keep Settings only — it's the one place they still need (change
  // email/phone, delete the account). Everything else is hidden until the
  // agent module actually exists.
  if (state === 'agent') return [{ key: 'settings', to: '/settings' }];
  return [
    // Owners get their master availability calendar (their times + upcoming viewings).
    ...(state === 'owner' ? [{ key: 'calendar', to: '/owner/calendar', icon: 'calendar' as const }] : []),
    { key: 'help', to: PLACEHOLDER, icon: 'help' },
    { key: 'settings', to: '/settings' },
  ];
}
